import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { auth, functions } from "../firebase-config";
import Hls from "hls.js";

const BUNNY_COST_PER_GB = 0.03;
const ESTIMATED_VIDEO_BITRATE_MBPS = 3;
const LESSON_COMPLETE_PERCENT = 90;
const SESSION_SNAPSHOT_INTERVAL_MS = 5 * 60 * 1000;

// Production HLS mode.
// HLS is always enabled for every student.

function convertMp4PathToHlsPlaylist(videoPath = "") {
  let cleanPath = String(videoPath)
    .replace(/^\/?virtualassistantpro\/?/i, "/")
    .replace(
      /^\/courses\/Real Estate Training Courses\//i,
      "/courses/Real-Estate-VA/"
    );

  if (!cleanPath.startsWith("/")) {
    cleanPath = `/${cleanPath}`;
  }

  return cleanPath.replace(/\.mp4$/i, "/playlist.m3u8");
}


function safeDecodeURIComponent(value = "") {
  const input = String(value || "");

  try {
    return decodeURIComponent(input);
  } catch (error) {
    // Keep malformed/literal-percent paths unchanged instead of crashing.
    return input;
  }
}

function normalizeProgressPath(value = "") {
  return safeDecodeURIComponent(value)
    .replace(/\\/g, "/")
    .replace(/^https?:\/\/[^/]+/i, "")
    .replace(/^\/?virtualassistantpro-hls\/?/i, "/")
    .replace(/^\/?virtualassistantpro\/?/i, "/")
    .replace(/\/+/g, "/")
    .replace(/\/+$/, "")
    .toLowerCase();
}

function getProgressPathAliases(videoPath = "") {
  const normalized = normalizeProgressPath(videoPath);
  const aliases = new Set([normalized]);

  const add = (value) => {
    const next = normalizeProgressPath(value);
    if (next) aliases.add(next);
  };

  // Old MP4 course path <-> new HLS Real Estate folder name.
  if (normalized.startsWith("/courses/real estate training courses/")) {
    add(
      normalized.replace(
        /^\/courses\/real estate training courses\//,
        "/courses/real-estate-va/"
      )
    );
  }

  if (normalized.startsWith("/courses/real-estate-va/")) {
    add(
      normalized.replace(
        /^\/courses\/real-estate-va\//,
        "/courses/real estate training courses/"
      )
    );
  }

  // Existing students may have progress stored as either the old .mp4 path
  // or the new HLS /playlist.m3u8 path. Treat them as the same lesson.
  Array.from(aliases).forEach((path) => {
    if (path.endsWith(".mp4")) {
      add(path.replace(/\.mp4$/i, "/playlist.m3u8"));
    } else if (path.endsWith("/playlist.m3u8")) {
      add(path.replace(/\/playlist\.m3u8$/i, ".mp4"));
    }
  });

  return Array.from(aliases);
}

function findProgressForPath(progressMap = {}, videoPath = "") {
  if (!videoPath) return null;

  if (progressMap[videoPath]) {
    return progressMap[videoPath];
  }

  const wantedAliases = new Set(getProgressPathAliases(videoPath));

  for (const [storedPath, progress] of Object.entries(progressMap || {})) {
    const storedAliases = getProgressPathAliases(storedPath);

    if (storedAliases.some((alias) => wantedAliases.has(alias))) {
      return progress;
    }
  }

  return null;
}

export default function StudentCourse({ courses = [], adminPreview = false }) {
  const { courseId } = useParams();
  const navigate = useNavigate();

  const dashboardPath = adminPreview
    ? "/admin/student-preview"
    : "/student/dashboard";

  const videoRef = useRef(null);
  const sessionRef = useRef(null);
  const watchedSecondsRef = useRef(new Set());
  const progressSaveTimerRef = useRef(null);
  const pathHistoryRef = useRef([]);
  const savingSessionRef = useRef(false);
  const finalizedSessionIdsRef = useRef(new Set());
  const finalizingPromiseRef = useRef(null);
  const savingSnapshotRef = useRef(false);
  const lastSnapshotAtMsRef = useRef(0);
  const hlsRef = useRef(null);

  const course = useMemo(
    () => courses.find((c) => c.id === courseId),
    [courses, courseId]
  );

  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [items, setItems] = useState([]);
  const [currentPath, setCurrentPath] = useState("");
  const [activeLesson, setActiveLesson] = useState(null);
  const [signedVideoUrl, setSignedVideoUrl] = useState(null);
  const [videoLoading, setVideoLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [lessonProgress, setLessonProgress] = useState({});
  const [playbackError, setPlaybackError] = useState("");

  const userRef = useRef(null);
  const courseRef = useRef(null);
  const courseIdRef = useRef(courseId);
  const lessonProgressRef = useRef({});

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {
    courseRef.current = course;
  }, [course]);

  useEffect(() => {
    courseIdRef.current = courseId;
  }, [courseId]);

  useEffect(() => {
    lessonProgressRef.current = lessonProgress;
  }, [lessonProgress]);

  const rootPath = useMemo(() => {
    if (!course || !course.name) return "";

    const normalizedName = course.name.toLowerCase().replace(/-/g, " ");

    if (normalizedName.includes("real estate")) {
      return `courses/Real-Estate-VA/`;
    }

    return `courses/${course.name}/`;
  }, [course]);

  const formatTime = (seconds = 0) => {
    const safeSeconds = Math.max(0, Math.floor(seconds));
    const minutes = Math.floor(safeSeconds / 60);
    const remainingSeconds = safeSeconds % 60;
    return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
  };

  const createPlaybackSessionId = () => {
    if (window.crypto?.randomUUID) {
      return window.crypto.randomUUID();
    }

    return `session_${Date.now()}_${Math.random()
      .toString(36)
      .slice(2, 12)}`;
  };

  const getSessionAnalytics = useCallback((reason = "unknown") => {
    const session = sessionRef.current;
    const currentUser = userRef.current;
    const currentCourseId = courseIdRef.current;

    if (!session) return null;

    const watchSeconds = watchedSecondsRef.current.size;
    const durationSeconds = session.durationSeconds || 0;

    const completionPercent =
      durationSeconds > 0
        ? Math.min(100, Math.round((watchSeconds / durationSeconds) * 100))
        : 0;

    const estimatedMB = (ESTIMATED_VIDEO_BITRATE_MBPS * watchSeconds) / 8;
    const estimatedGB = estimatedMB / 1024;
    const estimatedCost = estimatedGB * BUNNY_COST_PER_GB;

    return {
      sessionId: session.sessionId || "",
      sessionVersion: 2,
      reason,
      uid: currentUser?.uid || "",
      email: currentUser?.email || "",
      courseId: currentCourseId,
      videoPath: session.videoPath,
      lessonTitle: session.lessonTitle,
      watchSeconds,
      durationSeconds,
      completionPercent,
      playCount: session.playCount || 0,
      pauseCount: session.pauseCount || 0,
      seekCount: session.seekCount || 0,
      estimatedMB: Number(estimatedMB.toFixed(2)),
      estimatedGB: Number(estimatedGB.toFixed(4)),
      estimatedCost: Number(estimatedCost.toFixed(4)),
      bunnyCostPerGB: BUNNY_COST_PER_GB,
      estimatedBitrateMbps: ESTIMATED_VIDEO_BITRATE_MBPS,
      clientUserAgent: window.navigator?.userAgent || "",
      clientPlatform: window.navigator?.platform || "",
      startedAtMs: session.startedAtMs,
      endedAtMs: Date.now(),
    };
  }, []);

  const saveLessonProgress = useCallback(async (reason = "progress_update") => {
    if (adminPreview) return;

    const currentUser = userRef.current;
    const currentCourse = courseRef.current;
    const currentLessonProgress = lessonProgressRef.current || {};

    if (!currentUser || !currentCourse || !sessionRef.current || !videoRef.current) return;

    const video = videoRef.current;
    const session = sessionRef.current;

    const currentTime = Math.floor(video.currentTime || 0);
    const durationSeconds = Math.floor(
      video.duration || session.durationSeconds || 0
    );

    if (!session.videoPath || durationSeconds <= 0) return;

    const watchSeconds = watchedSecondsRef.current.size;

    const currentSessionPercent = Math.min(
      100,
      Math.round((watchSeconds / durationSeconds) * 100)
    );

    const previousPercent =
      findProgressForPath(currentLessonProgress, session.videoPath)?.completionPercent || 0;

    const completionPercent = Math.max(
      previousPercent,
      currentSessionPercent
    );

    const completed = completionPercent >= LESSON_COMPLETE_PERCENT;

    const progressData = {
      courseId: currentCourse.id,
      courseName: currentCourse.name || "",
      lessonTitle: session.lessonTitle,
      videoPath: session.videoPath,
      completionPercent,
      completed,
      watchSeconds,
      durationSeconds,
      lastPositionSeconds: currentTime,
      currentTime,
      reason,
    };

    try {
      const saveLessonProgressFn = httpsCallable(
        functions,
        "saveLessonProgress"
      );

      await saveLessonProgressFn(progressData);

      setLessonProgress((prev) => ({
        ...prev,
        [session.videoPath]: {
          ...(prev[session.videoPath] || {}),
          ...progressData,
        },
      }));
    } catch (err) {
      console.error("Save lesson progress failed:", err);
    }
  }, [adminPreview]);

  const finalizeLocalVideoSession = useCallback(async (reason = "unknown") => {
    const session = sessionRef.current;

    if (!session) return;

    if (adminPreview) {
      sessionRef.current = null;
      watchedSecondsRef.current = new Set();
      return;
    }

    const sessionId = session.sessionId;

    if (finalizedSessionIdsRef.current.has(sessionId)) {
      return;
    }

    if (savingSessionRef.current && finalizingPromiseRef.current) {
      return finalizingPromiseRef.current;
    }

    savingSessionRef.current = true;

    finalizingPromiseRef.current = (async () => {
      try {
        await saveLessonProgress(reason);

        const analytics = getSessionAnalytics(reason);
        if (!analytics) return;

        if (analytics.watchSeconds > 0 || analytics.playCount > 0) {
          try {
            const saveVideoSession = httpsCallable(functions, "saveVideoSession");
            await saveVideoSession(analytics);
          } catch (err) {
            console.error("Save video session failed:", err);
          }
        }

        finalizedSessionIdsRef.current.add(sessionId);

        if (sessionRef.current?.sessionId === sessionId) {
          sessionRef.current = null;
          watchedSecondsRef.current = new Set();
        }
      } finally {
        savingSessionRef.current = false;
        finalizingPromiseRef.current = null;
      }
    })();

    return finalizingPromiseRef.current;
  }, [adminPreview, getSessionAnalytics, saveLessonProgress]);

  const saveCurrentSessionSnapshot = useCallback(async (reason = "session_snapshot", force = false) => {
    if (adminPreview) return;

    const session = sessionRef.current;

    if (!session) return;
    if (savingSessionRef.current) return;
    if (savingSnapshotRef.current) return;
    if (finalizedSessionIdsRef.current.has(session.sessionId)) return;

    const nowMs = Date.now();

    if (!force && nowMs - lastSnapshotAtMsRef.current < SESSION_SNAPSHOT_INTERVAL_MS) {
      return;
    }

    // Throttle immediately so frequent timeupdate events do not trigger
    // multiple Firebase calls while the first snapshot is still running.
    lastSnapshotAtMsRef.current = nowMs;
    savingSnapshotRef.current = true;

    try {
      await saveLessonProgress(reason);

      const analytics = getSessionAnalytics(reason);
      if (!analytics) return;

      if (analytics.watchSeconds > 0 || analytics.playCount > 0) {
        const saveVideoSessionSnapshot = httpsCallable(
          functions,
          "saveVideoSessionSnapshot"
        );

        await saveVideoSessionSnapshot(analytics);
      }
    } catch (err) {
      console.error("Save video session snapshot failed:", err);
    } finally {
      savingSnapshotRef.current = false;
    }
  }, [adminPreview, getSessionAnalytics, saveLessonProgress]);

  const handleBack = async () => {
    if (pathHistoryRef.current.length > 0) {
      await finalizeLocalVideoSession("folder_back");

      const previousPath = pathHistoryRef.current.pop();

      setCurrentPath(previousPath);
      setActiveLesson(null);
      setSignedVideoUrl(null);

      return;
    }

    navigate(dashboardPath);
  };

  useEffect(() => {
    if (rootPath) setCurrentPath(rootPath);
  }, [rootPath]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
      setAuthReady(true);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!authReady || !user || !course) return;

    if (adminPreview) {
      setLessonProgress({});
      return;
    }

    const loadLessonProgress = async () => {
      try {
        const getMyLessonProgress = httpsCallable(
          functions,
          "getMyLessonProgress"
        );

        const result = await getMyLessonProgress();

        const progressList = Array.isArray(result?.data?.progress)
          ? result.data.progress
          : [];

        const progressMap = {};

        progressList.forEach((item) => {
          if (item.videoPath) {
            progressMap[item.videoPath] = item;
          }
        });

        setLessonProgress(progressMap);
      } catch (err) {
        console.error("Load lesson progress failed:", err);
      }
    };

    loadLessonProgress();
  }, [adminPreview, authReady, user, course]);

  useEffect(() => {
    if (!authReady || !user || adminPreview) return;

    const heartbeatStudent = httpsCallable(functions, "heartbeatStudent");
    const endStudentSession = httpsCallable(functions, "endStudentSession");

    heartbeatStudent().catch((err) => {
      console.error("Initial heartbeat failed:", err);
    });

    const interval = setInterval(() => {
      heartbeatStudent().catch((err) => {
        console.error("Heartbeat failed:", err);
      });
    }, 30000);

    const handleUnload = () => {
      if (!savingSessionRef.current) {
        finalizeLocalVideoSession("browser_close");
      }
      endStudentSession().catch(() => {});
    };

    window.addEventListener("beforeunload", handleUnload);

    return () => {
      clearInterval(interval);
      window.removeEventListener("beforeunload", handleUnload);
      finalizeLocalVideoSession("page_leave");
      endStudentSession().catch(() => {});
    };
  }, [adminPreview, authReady, user, finalizeLocalVideoSession]);

  const sortedItems = useMemo(() => {
    return [...items]
      .filter((item) => {
        if (item.IsDirectory || item.IsHlsLesson) return true;

        const lowerName = String(item.ObjectName || "").toLowerCase();
        return !lowerName.endsWith(".ts") && lowerName !== "playlist.m3u8";
      })
      .sort((a, b) => {
      if (a.IsDirectory && !b.IsDirectory) return -1;
      if (!a.IsDirectory && b.IsDirectory) return 1;

      const getNumber = (name) => {
        const match = name.match(/^(\d+)/);
        return match ? parseInt(match[1], 10) : Infinity;
      };

      const numA = getNumber(a.ObjectName);
      const numB = getNumber(b.ObjectName);

      if (numA !== numB) return numA - numB;

        return a.ObjectName.localeCompare(b.ObjectName, undefined, {
          numeric: true,
        });
      });
  }, [items]);

  const formatTitle = (name) => {
    return name
      .replace(/\.(mp4|m3u8|ts)$/i, "")
      .replace(/^module-\d+-/i, "")
      .replace(/-/g, " ")
      .replace(/_/g, " ");
  };

  useEffect(() => {
    if (!authReady || !user || !course || !currentPath) return;

    const loadBunnyContent = async () => {
      setLoading(true);

      try {
        const listCourseContent = httpsCallable(functions, "listCourseContent");

        const apiPath = currentPath.startsWith("/")
          ? currentPath.substring(1)
          : currentPath;

        const result = await listCourseContent({
          path: apiPath,
          courseId: course.id,
          courseName: course.name || "",
          currentPath,
          adminPreview,
        });

        setItems(Array.isArray(result?.data?.items) ? result.data.items : []);
      } catch (err) {
        console.error("Failed to scan Bunny Storage:", err);
        setItems([]);
      } finally {
        setLoading(false);
      }
    };

    loadBunnyContent();
  }, [adminPreview, authReady, user, course, currentPath]);

  const handleVideoClick = async (item) => {
    if (videoLoading) return;

    const rawPath = `${item.Path}${item.ObjectName}`;
    const fallbackPath = rawPath.startsWith("/") ? rawPath : `/${rawPath}`;

    // HLS-native listCourseContent supplies both paths:
    // - OriginalVideoPath keeps existing progress/history compatible.
    // - HlsPlaylistPath points to the actual HLS playlist in the new storage.
    const finalPath = item.OriginalVideoPath || fallbackPath;

    const useNewHlsLibrary =
      item.IsHlsLesson === true ||
      Boolean(item.HlsPlaylistPath) ||
      finalPath.toLowerCase().endsWith(".mp4");

    const playbackPath = item.HlsPlaylistPath
      ? item.HlsPlaylistPath
      : useNewHlsLibrary
      ? convertMp4PathToHlsPlaylist(finalPath)
      : finalPath;

    // Compare using the original lesson path so clicking the same lesson
    // resumes instead of creating another session.
    if (activeLesson?.path === finalPath) {
      if (videoRef.current) {
        videoRef.current.play().catch(() => {});
      }
      return;
    }

    try {
      await finalizeLocalVideoSession("video_switched");

      setVideoLoading(true);
      setPlaybackError("");
      setSignedVideoUrl(null);

      const functionName = useNewHlsLibrary
        ? "getSignedBunnyHlsTestUrl"
        : "getSignedBunnyUrl";

      const getSignedUrl = httpsCallable(functions, functionName);

      const lessonTitle = formatTitle(item.ObjectName);

      const result = await getSignedUrl({
        videoPath: playbackPath,
        originalVideoPath: finalPath,
        courseId: course.id,
        courseName: course.name || "",
        lessonTitle,
        adminPreview,
        // Signed HLS folder token protects playlist.m3u8 and all .ts segments.
        useSigned: true,
      });

      const lessonData = {
        title: lessonTitle,
        // Keep the original MP4 path for existing progress and analytics.
        path: finalPath,
        // Use this separate path only to decide MP4 vs HLS playback.
        playbackPath,
        isNewHlsTest: useNewHlsLibrary,
      };

      sessionRef.current = {
        sessionId: createPlaybackSessionId(),
        sessionVersion: 2,
        videoPath: finalPath,
        lessonTitle: lessonData.title,
        courseId: course.id,
        startedAtMs: Date.now(),
        durationSeconds: 0,
        playCount: 0,
        pauseCount: 0,
        seekCount: 0,
      };

      watchedSecondsRef.current = new Set();
      lastSnapshotAtMsRef.current = Date.now();

      setActiveLesson(lessonData);
      setSignedVideoUrl(result?.data?.signedUrl || null);
    } catch (err) {
      console.error("Signature Error:", err);

      const code = err?.code || "";
      const message = err?.message || "";

      if (
        code.includes("permission-denied") ||
        message.toLowerCase().includes("access") ||
        message.toLowerCase().includes("disabled") ||
        message.toLowerCase().includes("revoked")
      ) {
        alert("Your course access has been revoked or disabled.");
        navigate(dashboardPath, { replace: true });
        return;
      }

      alert("Error loading video playback.");
    } finally {
      setVideoLoading(false);
    }
  };

  useEffect(() => {
    const video = videoRef.current;

    if (!video || !signedVideoUrl || !activeLesson?.path) return undefined;

    setPlaybackError("");

    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    const playbackPath =
      activeLesson.playbackPath || activeLesson.path;

    const isHlsPlaylist = playbackPath.toLowerCase().endsWith(".m3u8");

    if (!isHlsPlaylist) {
      video.src = signedVideoUrl;
      video.load();
      video.play().catch(() => {});
      return undefined;
    }

    if (Hls.isSupported()) {
      const hls = new Hls({
        enableWorker: true,
        lowLatencyMode: false,
        backBufferLength: 90,
      });

      hlsRef.current = hls;
      hls.loadSource(signedVideoUrl);
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play().catch(() => {});
      });

      hls.on(Hls.Events.ERROR, (_event, data) => {
        // hls.js can report recoverable events such as bufferStalledError.
        // Ignore non-fatal events here; playback can continue normally.
        if (!data?.fatal) return;

        console.error("Fatal HLS playback error:", data);

        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          hls.startLoad();
          return;
        }

        if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          hls.recoverMediaError();
          return;
        }

        setPlaybackError(
          "The HLS video could not start. Please try again or contact support."
        );
        hls.destroy();

        if (hlsRef.current === hls) {
          hlsRef.current = null;
        }
      });

      return () => {
        hls.destroy();

        if (hlsRef.current === hls) {
          hlsRef.current = null;
        }
      };
    }

    if (video.canPlayType("application/vnd.apple.mpegurl")) {
      // Native HLS fallback. This is mainly for Safari.
      video.src = signedVideoUrl;
      video.load();
      video.play().catch(() => {});
      return undefined;
    }

    setPlaybackError(
      "This browser does not support HLS playback."
    );

    return undefined;
  }, [signedVideoUrl, activeLesson]);

  const handleLoadedMetadata = (e) => {
    if (!sessionRef.current) return;

    const duration = Math.floor(e.currentTarget.duration || 0);
    sessionRef.current.durationSeconds = duration;

    const savedProgress = findProgressForPath(lessonProgress, sessionRef.current.videoPath);

    const resumeTime = Math.floor(
      savedProgress?.lastPositionSeconds ??
        savedProgress?.currentTime ??
        0
    );

    if (savedProgress && resumeTime > 5 && resumeTime < duration - 5) {
      console.log("Resuming video at", resumeTime, "seconds");
      e.currentTarget.currentTime = resumeTime;
    }
  };

  const handleTimeUpdate = (e) => {
    if (!sessionRef.current) return;

    const currentSecond = Math.floor(e.currentTarget.currentTime || 0);
    const duration = Math.floor(e.currentTarget.duration || 0);

    if (duration > 0 && currentSecond >= 0 && currentSecond <= duration) {
      watchedSecondsRef.current.add(currentSecond);
    }

    if (!progressSaveTimerRef.current) {
      progressSaveTimerRef.current = setTimeout(() => {
        saveLessonProgress("time_update");
        progressSaveTimerRef.current = null;
      }, 30000);
    }

    if (!e.currentTarget.paused) {
      saveCurrentSessionSnapshot("five_minute_autosave");
    }
  };

  const handlePlay = () => {
    if (!sessionRef.current) return;
    sessionRef.current.playCount += 1;
  };

  const handlePause = () => {
    if (!sessionRef.current) return;
    sessionRef.current.pauseCount += 1;
    saveCurrentSessionSnapshot("video_paused", true);
  };

  const handleSeeking = () => {
    if (!sessionRef.current) return;
    sessionRef.current.seekCount += 1;
  };

  const activeLessonProgress = activeLesson?.path
    ? findProgressForPath(lessonProgress, activeLesson.path)
    : null;

  const handleEnded = async () => {
    if (!sessionRef.current || !videoRef.current) return;

    const duration = Math.floor(videoRef.current.duration || 0);

    for (let i = 0; i <= duration; i += 1) {
      watchedSecondsRef.current.add(i);
    }

    await saveLessonProgress("video_ended");
    await finalizeLocalVideoSession("video_ended");
  };

  if (!course) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        Course not found.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6 md:p-10">
      {adminPreview && (
        <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 rounded-2xl border border-orange-500/50 bg-orange-950/40 p-4">
          <div>
            <p className="text-xs font-black uppercase tracking-widest text-orange-300">
              Admin Preview Mode
            </p>
            <p className="mt-1 text-sm text-gray-200">
              You are testing this course as an administrator. Lesson progress,
              heartbeat, and video-session writes are disabled.
            </p>
          </div>

          <button
            type="button"
            onClick={() => navigate("/admin/dashboard")}
            className="rounded-xl bg-orange-500 px-5 py-2.5 font-black text-white transition hover:bg-orange-600"
          >
            ← Back to Admin Dashboard
          </button>
        </div>
      )}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-orange-400">{course.name}</h1>
          <p className="text-[10px] text-gray-500 mt-1 uppercase tracking-widest">
            Storage: {currentPath}
          </p>

        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(dashboardPath)}
            className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-5 py-2.5 rounded-xl shadow-lg transition-all duration-200 hover:scale-105 active:scale-95"
          >
            🏠 Home
          </button>

          <button
            onClick={handleBack}
            className="flex items-center gap-2 bg-gray-700 hover:bg-gray-600 text-white font-semibold px-5 py-2.5 rounded-xl shadow-lg transition-all duration-200 hover:scale-105 active:scale-95"
          >
            ← Back
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-gray-400 py-10 flex items-center gap-3">
          <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-orange-500"></div>
          Scanning files...
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1 bg-gray-800 rounded-xl p-4 max-h-[75vh] overflow-y-auto border border-gray-700 shadow-2xl">
            <h2 className="text-xl font-semibold mb-4 border-b border-gray-700 pb-2">
              Course Content
            </h2>

            {sortedItems.length === 0 ? (
              <p className="text-gray-500 italic p-4 text-center">
                No content found in this folder.
              </p>
            ) : (
              sortedItems.map((item, idx) => {
                const rawPath = `${item.Path}${item.ObjectName}`;
                const fallbackPath = rawPath.startsWith("/") ? rawPath : `/${rawPath}`;
                const finalPath = item.OriginalVideoPath || fallbackPath;
                const progress = findProgressForPath(lessonProgress, finalPath);
                const isCompleted = progress?.completed;
                const isCurrent = activeLesson?.title === formatTitle(item.ObjectName);
                const resumePosition =
                  progress?.lastPositionSeconds || progress?.currentTime || 0;

                return (
                  <div key={item.Guid || idx} className="mb-2">
                    {item.IsDirectory ? (
                      <button
                        onClick={async () => {
                          await finalizeLocalVideoSession("folder_changed");

                          const folderRawPath = `${item.Path}${item.ObjectName}/`;
                          const cleanPath = folderRawPath
                            .replace(/^\/?virtualassistantpro-hls\//i, "")
                            .replace(/^\/?virtualassistantpro\//i, "");

                          pathHistoryRef.current.push(currentPath);
                          setCurrentPath(cleanPath);
                          setActiveLesson(null);
                          setSignedVideoUrl(null);
                        }}
                        className="w-full text-left mt-2 mb-1 p-3 bg-gray-900 hover:bg-gray-700 rounded border-l-4 border-orange-500 transition group"
                      >
                        <p className="text-orange-400 font-medium flex justify-between items-center">
                          📁 {formatTitle(item.ObjectName)}
                          <span className="text-xs text-gray-600 group-hover:text-orange-300">
                            Open →
                          </span>
                        </p>
                      </button>
                    ) : (
                      <button
                        onClick={() => handleVideoClick(item)}
                        className={`block w-full text-left px-3 py-3 rounded mb-1 transition text-sm ${
                          isCurrent
                            ? "bg-orange-600 shadow-lg font-bold"
                            : "bg-gray-700 hover:bg-gray-600"
                        }`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <span>
                            {isCompleted ? "✔" : isCurrent ? "▶" : "○"}{" "}
                            {formatTitle(item.ObjectName)}
                          </span>

                          {progress?.completionPercent > 0 && (
                            <span className="text-[11px] text-gray-300 whitespace-nowrap">
                              {progress.completionPercent}%
                            </span>
                          )}
                        </div>

                        {resumePosition > 5 && !isCompleted && (
                          <p className="text-[11px] text-gray-300 mt-1 ml-5">
                            Resume at {formatTime(resumePosition)}
                          </p>
                        )}
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <div className="lg:col-span-2 bg-gray-800 rounded-xl p-6 flex flex-col border border-gray-700 shadow-2xl">
            {!activeLesson ? (
              <div className="py-20 text-center text-gray-500 border-2 border-dashed border-gray-700 rounded-lg bg-gray-900/50">
                Select a video lesson to start
              </div>
            ) : videoLoading ? (
              <div className="py-20 text-center text-gray-400 flex flex-col items-center gap-4">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-orange-500"></div>
                Authorizing Secure Stream...
              </div>
            ) : signedVideoUrl ? (
              <>
                <div className="flex items-center justify-between gap-4 mb-4">
                  <h3 className="text-xl font-semibold text-orange-400">
                    {activeLesson.title}
                  </h3>

                  {activeLessonProgress?.completed && (
                    <span className="bg-green-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                      ✔ Completed
                    </span>
                  )}
                </div>

                {activeLessonProgress?.completionPercent > 0 && (
                  <div className="mb-4">
                    <div className="flex justify-between text-xs text-gray-300 mb-1">
                      <span>Lesson Progress</span>
                      <span>
                        {activeLessonProgress?.completionPercent || 0}%
                      </span>
                    </div>

                    <div className="w-full bg-gray-700 rounded-full h-2 overflow-hidden">
                      <div
                        className="bg-green-500 h-2 rounded-full transition-all"
                        style={{
                          width: `${
                            activeLessonProgress?.completionPercent || 0
                          }%`,
                        }}
                      ></div>
                    </div>
                  </div>
                )}

                <div className="relative w-full aspect-video rounded-lg overflow-hidden bg-black shadow-2xl border border-gray-700">
                  <video
                    ref={videoRef}
                    key={signedVideoUrl}
                    controls
                    controlsList="nodownload"
                    disablePictureInPicture
                    autoPlay
                    playsInline
                    className="w-full h-full"
                    onLoadedMetadata={handleLoadedMetadata}
                    onTimeUpdate={handleTimeUpdate}
                    onPlay={handlePlay}
                    onPause={handlePause}
                    onSeeking={handleSeeking}
                    onEnded={handleEnded}
                  />
                </div>

                {playbackError && (
                  <div className="mt-4 rounded-lg border border-red-500/40 bg-red-900/30 p-3 text-sm text-red-200">
                    {playbackError}
                  </div>
                )}
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}